import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Share2, UserPlus, UserCheck, Cake, MapPin, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { WishlistCard } from "@/components/WishlistCard";
import { ShareDialog } from "@/components/ShareDialog";

export default function PublicProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/users/${username}`);
      setProfile(res.data);
      setError(null);
    } catch {
      setError(true);
    }
  }, [username]);

  useEffect(() => { load(); }, [load]);

  const follow = async () => {
    if (!user) { navigate("/giris"); return; }
    try {
      const res = await api.post(`/users/${username}/follow`);
      toast.success(res.data.following ? t("now_following") : t("unfollowed"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    }
  };

  if (error) return <div className="text-center py-20 text-muted-foreground" data-testid="profile-error">{t("user_notfound")}</div>;
  if (!profile) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const days = profile.birthday ? (() => {
    const bd = new Date(profile.birthday);
    const today = new Date();
    let next = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
    if (next < today.setHours(0, 0, 0, 0)) next = new Date(today.getFullYear() + 1, bd.getMonth(), bd.getDate());
    return Math.round((next - new Date().setHours(0, 0, 0, 0)) / 86400000);
  })() : null;

  const firstName = profile.name.split(" ")[0];
  const listsTitle = profile.is_me ? t("my_lists") : t("users_lists", { name: firstName });

  return (
    <div data-testid="public-profile-page">
      <div className="bg-card rounded-3xl border border-border/50 p-6 sm:p-8 mb-8 fade-up">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <Avatar className="w-20 h-20 sm:w-24 sm:h-24 border-4 border-primary/20">
            <AvatarImage src={profile.picture} />
            <AvatarFallback className="bg-secondary text-2xl font-heading font-bold">{profile.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold" data-testid="profile-name">{profile.name}</h1>
            <p className="text-muted-foreground text-sm">@{profile.username}</p>
            {profile.bio && <p className="text-sm mt-2">{profile.bio}</p>}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
              {profile.city && <span className="flex items-center gap-1"><MapPin size={13} /> {profile.city}</span>}
              {days !== null && (
                <span className="flex items-center gap-1 text-primary font-semibold">
                  <Cake size={13} /> {days === 0 ? t("bday_today") : t("bday_in", { d: days })}
                </span>
              )}
              {profile.social_links?.instagram && (
                <a href={`https://instagram.com/${profile.social_links.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-primary">
                  <Instagram size={13} /> {profile.social_links.instagram}
                </a>
              )}
              <span><b className="text-foreground">{profile.follower_count}</b> {t("followers")}</span>
              <span><b className="text-foreground">{profile.following_count}</b> {t("following_n")}</span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!profile.is_me && (
              <Button onClick={follow} className="rounded-full active:scale-95 transition-transform"
                variant={profile.is_following ? "outline" : "default"} data-testid="profile-follow-btn">
                {profile.is_following ? <><UserCheck size={15} className="mr-1" /> {t("following_btn")}</> : <><UserPlus size={15} className="mr-1" /> {t("follow")}</>}
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setShareOpen(true)} data-testid="profile-share-btn">
              <Share2 size={15} className="mr-1" /> {t("share")}
            </Button>
          </div>
        </div>
      </div>

      <h2 className="font-heading text-lg font-semibold mb-4">{listsTitle} 🎁</h2>
      {profile.wishlists.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground">
          {t("no_visible_lists")}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {profile.wishlists.map((wl, i) => <WishlistCard key={wl.wishlist_id} wishlist={wl} delay={i * 0.05} />)}
        </div>
      )}

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen}
        url={`${window.location.origin}/api/share/u/${profile.username}`}
        title={lang === "tr" ? `${profile.name}'in İstek Listesi` : `${profile.name}'s Wishlist`} />
    </div>
  );
}
